import {
  addSummarySheet,
  autoFitColumns,
  createWorkbook,
  downloadWorkbook,
  styleBodyRow,
  styleHeaderRow,
} from '../../platform/excelExport';
import { resolveMessageAuthorName } from '../../platform/displayName';

function getVoterDisplayName(ansDoc) {
  return resolveMessageAuthorName(ansDoc) || 'Neznano';
}

function addInfoSheet(workbook, poll) {
  addSummarySheet(workbook, {
    title: poll.title,
    rows: [
      ['Status', poll.active ? 'Aktivna' : 'Zaključena'],
      ['Vprašanj', poll.questions.length],
      ['Zasebnost', poll.anonymous === true ? 'Anonimna' : 'Identificirana'],
      ['Enota', poll.unitName || 'Splošno'],
      ['Ustvaril', poll.displayName || '—'],
    ],
  });
}

function addResultsSheet(workbook, poll, results) {
  const choiceQuestions = poll.questions.filter((q) => q.type !== 'text');
  if (choiceQuestions.length === 0) {
    return;
  }

  const sheet = workbook.addWorksheet('Rezultati');
  sheet.columns = [
    { header: 'Vprašanje', key: 'question', width: 40 },
    { header: 'Možnost', key: 'option', width: 36 },
    { header: 'Glasovi', key: 'votes', width: 12 },
  ];

  styleHeaderRow(sheet.getRow(1));

  let rowIndex = 2;
  poll.questions.forEach((question, qIndex) => {
    if (question.type === 'text') {
      return;
    }

    const questionResults = results[qIndex] || {};
    const allOptions = new Set([
      ...(question.options || []),
      ...Object.keys(questionResults),
    ]);

    sheet.getRow(rowIndex).getCell(1).value = question.question;
    sheet.getRow(rowIndex).getCell(1).font = { bold: true };
    rowIndex += 1;

    allOptions.forEach((opt) => {
      const trimmed = (opt || '').trim();
      if (!trimmed) {
        return;
      }
      const row = sheet.getRow(rowIndex);
      row.getCell(2).value = trimmed;
      row.getCell(3).value = questionResults[trimmed] || 0;
      styleBodyRow(row, { alt: rowIndex % 2 === 0 });
      rowIndex += 1;
    });

    rowIndex += 1;
  });

  autoFitColumns(sheet, { question: 44, option: 36, votes: 12 });
}

function addTextAnswersSheet(workbook, poll, textAnswers) {
  const textQuestions = poll.questions.filter((q) => q.type === 'text');
  if (textQuestions.length === 0) {
    return;
  }

  const sheet = workbook.addWorksheet('Tekstovni odgovori');
  sheet.columns = [
    { header: 'Vprašanje', key: 'question', width: 40 },
    { header: 'Odgovor', key: 'answer', width: 48 },
  ];

  styleHeaderRow(sheet.getRow(1));

  let rowIndex = 2;
  poll.questions.forEach((question, qIndex) => {
    if (question.type !== 'text') {
      return;
    }

    const answers = textAnswers[qIndex] || [];
    if (answers.length === 0) {
      const row = sheet.getRow(rowIndex);
      row.getCell(1).value = question.question;
      row.getCell(2).value = '—';
      styleBodyRow(row);
      rowIndex += 1;
      return;
    }

    answers.forEach((answer) => {
      const row = sheet.getRow(rowIndex);
      row.getCell(1).value = question.question;
      row.getCell(2).value = answer;
      styleBodyRow(row, { alt: rowIndex % 2 === 0 });
      rowIndex += 1;
    });
  });

  autoFitColumns(sheet, { question: 44, answer: 48 });
}

function addVotersSheet(workbook, answersDocs) {
  if (answersDocs.length === 0) {
    return;
  }

  const sheet = workbook.addWorksheet('Seznam glasovalcev');
  sheet.columns = [
    { header: '#', key: 'index', width: 6 },
    { header: 'Ime', key: 'name', width: 32 },
  ];

  styleHeaderRow(sheet.getRow(1));

  answersDocs.forEach((ansDoc, index) => {
    const row = sheet.getRow(index + 2);
    row.getCell(1).value = index + 1;
    row.getCell(2).value = getVoterDisplayName(ansDoc);
    styleBodyRow(row, { alt: index % 2 === 1 });
  });

  autoFitColumns(sheet);
}

function addDetailsSheet(workbook, poll, answersDocs) {
  const sheet = workbook.addWorksheet('Podrobnosti');
  sheet.columns = [
    { header: 'Glasovalec', key: 'voter', width: 28 },
    { header: 'Vprašanje', key: 'question', width: 40 },
    { header: 'Odgovor', key: 'answer', width: 40 },
  ];

  styleHeaderRow(sheet.getRow(1));

  let rowIndex = 2;
  answersDocs.forEach((ansDoc) => {
    const voterName = getVoterDisplayName(ansDoc);
    ansDoc.answers.forEach((answerItem) => {
      const row = sheet.getRow(rowIndex);
      const questionText =
        poll.questions[answerItem.questionIndex]?.question || '—';
      const answerVal = Array.isArray(answerItem.answer)
        ? answerItem.answer.join(', ')
        : answerItem.answer;

      row.getCell(1).value = voterName;
      row.getCell(2).value = questionText;
      row.getCell(3).value = answerVal;
      styleBodyRow(row, { alt: rowIndex % 2 === 0 });
      rowIndex += 1;
    });
  });

  autoFitColumns(sheet);
}

export async function exportPollToExcel({
  poll,
  results,
  textAnswers,
  answersDocs,
}) {
  const workbook = createWorkbook();

  addInfoSheet(workbook, poll);
  addResultsSheet(workbook, poll, results);
  addTextAnswersSheet(workbook, poll, textAnswers);

  if (poll.anonymous !== true) {
    addVotersSheet(workbook, answersDocs);
    addDetailsSheet(workbook, poll, answersDocs);
  }

  await downloadWorkbook(workbook, `Anketa_${poll.title.replace(/\s+/g, '_')}.xlsx`);
}
